/* globals showModalInfo, DPI_STATS_TMOUT_MS */
import React, { useContext, useState } from "react";
import styled from "styled-components";
import Radio from "common/Radio.jsx";
import SettingsView, { NoContextMenu } from "common/SettingsView";
import { composeCommand, composeFlushCommand, checkWarnings, formatResult } from "./api";
import requestUserFile from "../ProvideFile";
import { requestMoment } from "views/Status/Billing/api";
import { YYYYMMDDHHmmssSSS } from "common/utils";
import BqnContext from "common/BqnContext";

const strategyOptions = [
  { value: "restore", label: "Restore backup." },
  { value: "merge", label: "Merge backup." },
];

const Interactive = styled.div`
  & > * {
    margin: 0.25em 1em;
  }
`;

const StrategyOption = styled.div`
  label {
    font-size: 12px !important;
    padding-right: 20px;
  }
`;

const WarningsWrapper = styled.div`
  margin-top: 20px;

    ul {
    list-style: none;
      padding: 0;
    }
  `;


const Warnings = ({ strategy }) => (
  <WarningsWrapper>
    WARNING:
    <ul>
      {strategy !== "restore" ? null : (
        <li>
          The current subscriber id's, subscriber-groups, and policies assigned
          or defined via Radius/REST/Billing will be replaced by those in the
          backup file.
        </li>
      )}
      {strategy !== "merge" ? null : (
        <li>
          To the current subscriber id's, subscriber-groups, and policies
          assigned or defined via Radius/REST/Billing, we will add those
          in the backup file.
        </li>
      )}
    </ul>
  </WarningsWrapper>
);

const LoadSettings = ({ state, onChange }) => (
  <>
    <Interactive>
      <Radio
        options={strategyOptions}
        name="strategy"
        defaultValue={state.strategy}
        itemWrapper={StrategyOption}
        onChange={onChange}
      />
    </Interactive>
    <Warnings strategy={state.strategy} />
  </>
);

const reportSuccess = (result) =>
  showModalInfo("Success", result);

const TMP_CONF_PATH = "/tmp/reload_attributes_";
const createFilePath = () => TMP_CONF_PATH + YYYYMMDDHHmmssSSS(new Date());

const saveToCLI = (filepath, contents) =>
  ifCl.transfer(contents, filepath)
    .catch((error, reason) => {
      throw `Error uploading subscriber attributes file: ${reason}`;
    });

const provideFile = () =>
  requestUserFile().then((contents) => saveToCLI(createFilePath(), contents));

const submitCommand = (command, isBatch = false) =>
  ifCl.run(command, { isBatch, timeout: DPI_STATS_TMOUT_MS });

const submitCommandKeepingPreviousResponse = (command) => (previousResponse) =>
  submitCommand(command).then( (/*ignored*/) => previousResponse );

const submitFlushCommand = ({bqnId}) =>
  submitCommandKeepingPreviousResponse(
    composeFlushCommand({bqnId})
  )

const nodeRequestGivenSettings = ({bqnId, mode, ...settings}) => filePath =>
  Promise.resolve(composeCommand({bqnId, ...settings, filePath}))
    .then(submitCommand)
    .then(checkWarnings)
    .then(submitFlushCommand({bqnId}))
    .then(mode === 'standalone' ? formatResult : reportForNode(bqnId))

const reportForNode = bqnId => response =>
  `BQN ${bqnId}:\n${
    response === '' 
    ? 'Subscriber attributes file loaded successfully'
    : formatResult(response)
  }`
  
const tellOfflineNodes = ids =>
  ids.length === 1
    ? 'node ' + ids[0] + ' is offline'
    : ('nodes ' + ids.join(', ') + ' are offline').replace(/,(?=[^,]*$)/, " and");

const thoseOffline = (node) => node.function === 'offline';

const allNodesShouldBeOnline = nodes =>
  (offline =>
    new Promise((resolve, reject) =>
      offline.length > 0
      ? reject(`All nodes should be online before proceeding: ${tellOfflineNodes(offline)}`)
      : resolve(nodes)
    )
  )(nodes.filter(thoseOffline).map(({bqnId}) => bqnId))

const clusterCheckReady = ({bqns = []}) =>
  allNodesShouldBeOnline(bqns)

const clusterRequestsGivenSettings = ({bqns = [], ...settings}) => filePath =>
  Promise.resolve(
    bqns.map(({bqnId}) => [
      bqnId,
      composeCommand({...settings, filePath, bqnId})
    ])
  )
  .then(nodesCommands =>
    Promise.all(
      nodesCommands.map(([bqnId, command]) => 
        submitCommand(command, true)
          .then(checkWarnings)
          .then(submitFlushCommand({bqnId}))
          .catch(error => error)
          .then(reportForNode(bqnId))
      ) 
    )
  )
  .then(results => results.join("\n"))

const LoadSubsAttributes = ({ defaults = {} }) => {
  const {
    bqnId,
    bqns,
    clusterStatus: { mode },
    loadClusterStatus,
  } = useContext(BqnContext);
  const [isBusy, setBusy] = useState(false);

  const notifyAndRevert = (error) => {
    error = error.message === undefined ? new Error(error) : error;
    throw error;
  };

  const markAsBusy = (response) => setBusy(true) || response;

  const markAsReady = (response) => setBusy(false) || response;

  const apply = ({strategy}) => {

    const settings = { strategy, mode, bqnId, bqns };

    const isSingleNode = (mode === "standalone" || bqnId !== "all");

    const doRequestGivenSettings = isSingleNode
      ? nodeRequestGivenSettings
      : clusterRequestsGivenSettings;

    const checkReady = isSingleNode
      ? () => Promise.resolve()
      : () => loadClusterStatus().then(clusterCheckReady);

    return checkReady()
      .then(provideFile)
      .then(markAsBusy)
      .then(doRequestGivenSettings(settings))
      .then(reportSuccess)
      .catch(notifyAndRevert)
      .finally(markAsReady);
  };

  const load = () =>
    requestMoment().then((moment) => ({
      ...defaults,
      moment,
      strategy: "restore",
      file: "",
      pristine: false,
    }));

  return (
    <SettingsView
      load={load}
      apply={apply}
      applyLabel={isBusy !== true ? "SELECT FILE" : "APPLYING..."}
      ContextMenu={NoContextMenu}
      noDiscard={true}
      noPristine={true}
      title="Load Backup of External Attributes and Subscriber Groups"
      applyActionClassName="hidden-to-operators"
      PendingChangesIndicator={null}
      subtitle={<small>Loading may take some time.</small>}
      id="viewBackupLoadSubsAttributes"
    >
      <LoadSettings />
    </SettingsView>
  );
};

export default LoadSubsAttributes;
